import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '@/contexts/AppContext.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { FileText, Plus, Search, Trash2, ArrowLeft } from 'lucide-react';
import { cn, formatDate } from '@/lib/utils.js';

interface NotesListSidebarProps {
  currentNoteId: string;
}

export function NotesListSidebar({ currentNoteId }: NotesListSidebarProps) {
  const { notes, openNote, createNewNote, deleteNote } = useAppContext();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteNote(id);
  };

  const filteredNotes = notes.filter((note) =>
    note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="w-80 bg-muted/30 border-r border-border/50 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center gap-2 mb-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate('/notes')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-sm font-semibold">All Notes</h2>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
      </div>

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-2">
        {filteredNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <Search className="h-10 w-10 text-muted-foreground mb-3 opacity-50" />
            <p className="text-sm text-muted-foreground">No notes found</p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredNotes.map((note) => (
              <div
                key={note.id}
                onClick={() => openNote(note)}
                className={cn(
                  'group relative p-3 rounded-lg cursor-pointer transition-colors',
                  note.id === currentNoteId
                    ? 'bg-primary/10 border border-primary/50'
                    : 'hover:bg-muted'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className={cn(
                        "h-3.5 w-3.5 flex-shrink-0",
                        note.id === currentNoteId ? "text-primary" : "text-muted-foreground"
                      )} />
                      <p className={cn(
                        "font-medium text-xs truncate",
                        note.id === currentNoteId ? "text-primary" : "text-foreground"
                      )}>
                        {note.title}
                      </p>
                    </div>
                    {note.content && (
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-1">
                        {note.content}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground/70">
                      {formatDate(note.updatedAt)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    onClick={(e) => handleDelete(note.id, e)}
                  >
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New Button */}
      <div className="p-3 border-t border-border/50">
        <Button
          className="w-full gap-2"
          size="sm"
          variant="default"
          onClick={createNewNote}
        >
          <Plus className="h-4 w-4" />
          New Note
        </Button>
      </div>
    </div>
  );
}
